/**
 * Attachment types for the chat document-attachment feature.
 *
 * `MessageAttachment` mirrors the backend `AttachmentOut` schema (persisted).
 * `AttachmentDraft` is the client-side in-flight shape while the user's file
 * is being signed → uploaded → scanned → ready.
 */

/**
 * Server attachment shape — mirrors the backend `AttachmentOut` wire JSON
 * **verbatim (snake_case)**. The thread-detail response (`GET
 * /chat/threads/{id}` → `ThreadMessage.attachments`) flows straight through
 * with no snake→camel transform, consistent with `ProductCardData` /
 * `MaterialsEvent` which also consume snake_case from the API.
 */
export interface MessageAttachment {
  id: string;
  kind: "image" | "pdf" | "xlsx" | "csv";
  content_type: string;
  filename: string | null;
  doc_type: "plan" | "invoice" | "item_list" | "other" | null;
  page_count: number | null;
  thumb_url: string | null;
  file_url: string | null;
}

export type AttachmentUploadState =
  | "uploading"
  | "scanning"
  | "ready"
  | "error";

/** Client-side pending attachment in the composer (pre-send). */
export interface AttachmentDraft {
  localId: string;
  file: File;
  kind: MessageAttachment["kind"];
  state: AttachmentUploadState;
  progress: number; // 0..1 during uploading
  uploadId: string | null; // set once signed+uploaded+clean
  thumbDataUrl: string | null; // local object URL for image preview
  errorCode: string | null;
}

const _KIND_BY_TYPE: Record<string, MessageAttachment["kind"]> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/csv": "csv",
};

export function kindForType(
  contentType: string,
): MessageAttachment["kind"] | null {
  return _KIND_BY_TYPE[contentType] ?? null;
}

export const ACCEPTED_TYPES: string[] = Object.keys(_KIND_BY_TYPE);
export const MAX_ATTACHMENTS = 5;

export const MAX_BYTES_BY_TYPE: Record<string, number> = {
  "application/pdf": 25 * 1024 * 1024,
};
export const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

export function maxBytesFor(contentType: string): number {
  return MAX_BYTES_BY_TYPE[contentType] ?? DEFAULT_MAX_BYTES;
}
